import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";

/**
 * Consentimiento de cookies real (no decorativo).
 * - Consent Mode v2: por defecto todo "denied" (script en <head> antes de cualquier etiqueta).
 * - Analítica y publicidad no se cargan hasta aceptar la categoría.
 * - Comprobante: versión del aviso + decisión + fecha, guardado en el navegador y en la base.
 * - "Preferencias de cookies" en el pie reabre este panel (evento mm:cookie-prefs).
 * - Rechazar nunca impide reservar ni pagar.
 */
export const CONSENT_VERSION = "cookies-2026-09-24";
const KEY = "mm:consent:v1";

type Cats = { preferences: boolean; analytics: boolean; marketing: boolean };
type Stored = Cats & { version: string; at: string; id: string };

type L = "es" | "en" | "fr" | "de" | "it" | "pt";
const TXT: Record<L, Record<string, string>> = {
  es: { title: "Cookies y privacidad", body: "Usamos cookies necesarias para la sesión, la seguridad, el carrito y el pago. Con tu permiso, también usaríamos cookies de preferencias, analítica y publicidad. Puedes cambiar tu decisión cuando quieras en \"Preferencias de cookies\".", accept: "Aceptar todas", reject: "Rechazar no necesarias", config: "Configurar", save: "Guardar selección", necessary: "Necesarias (siempre activas)", preferences: "Preferencias y funcionalidad", analytics: "Analítica", marketing: "Publicidad y remarketing", policy: "Política de cookies" },
  en: { title: "Cookies and privacy", body: "We use necessary cookies for your session, security, cart and payment. With your permission we would also use preference, analytics and advertising cookies. You can change your choice anytime under \"Cookie preferences\".", accept: "Accept all", reject: "Reject non-essential", config: "Customize", save: "Save selection", necessary: "Necessary (always on)", preferences: "Preferences and functionality", analytics: "Analytics", marketing: "Advertising and remarketing", policy: "Cookie policy" },
  fr: { title: "Cookies et confidentialité", body: "Nous utilisons des cookies nécessaires pour la session, la sécurité, le panier et le paiement. Avec votre accord, nous utiliserions aussi des cookies de préférences, d'analyse et de publicité. Vous pouvez modifier votre choix à tout moment dans « Préférences de cookies ».", accept: "Tout accepter", reject: "Refuser les non essentiels", config: "Personnaliser", save: "Enregistrer la sélection", necessary: "Nécessaires (toujours actifs)", preferences: "Préférences et fonctionnalités", analytics: "Analyse", marketing: "Publicité et remarketing", policy: "Politique de cookies" },
  de: { title: "Cookies und Datenschutz", body: "Wir verwenden notwendige Cookies für Sitzung, Sicherheit, Warenkorb und Zahlung. Mit deiner Zustimmung würden wir auch Präferenz-, Analyse- und Werbe-Cookies verwenden. Du kannst deine Wahl jederzeit unter „Cookie-Einstellungen“ ändern.", accept: "Alle akzeptieren", reject: "Nicht notwendige ablehnen", config: "Anpassen", save: "Auswahl speichern", necessary: "Notwendig (immer aktiv)", preferences: "Präferenzen und Funktionen", analytics: "Analyse", marketing: "Werbung und Remarketing", policy: "Cookie-Richtlinie" },
  it: { title: "Cookie e privacy", body: "Usiamo cookie necessari per sessione, sicurezza, carrello e pagamento. Con il tuo consenso useremmo anche cookie di preferenze, analisi e pubblicità. Puoi cambiare la tua scelta in qualsiasi momento in \"Preferenze cookie\".", accept: "Accetta tutti", reject: "Rifiuta non necessari", config: "Personalizza", save: "Salva selezione", necessary: "Necessari (sempre attivi)", preferences: "Preferenze e funzionalità", analytics: "Analisi", marketing: "Pubblicità e remarketing", policy: "Politica sui cookie" },
  pt: { title: "Cookies e privacidade", body: "Usamos cookies necessários para sessão, segurança, carrinho e pagamento. Com sua permissão, também usaríamos cookies de preferências, análise e publicidade. Você pode mudar sua escolha a qualquer momento em \"Preferências de cookies\".", accept: "Aceitar todos", reject: "Rejeitar não necessários", config: "Configurar", save: "Salvar seleção", necessary: "Necessários (sempre ativos)", preferences: "Preferências e funcionalidade", analytics: "Análise", marketing: "Publicidade e remarketing", policy: "Política de cookies" },
};

const COOKIE = "mm_consent";

function valid(s: Stored | null): Stored | null {
  return s && s.version === CONSENT_VERSION ? s : null; // nueva versión → volver a preguntar
}

function readCookie(): Stored | null {
  try {
    const m = document.cookie.split("; ").find((p) => p.startsWith(COOKIE + "="));
    if (!m) return null;
    return valid(JSON.parse(decodeURIComponent(m.slice(COOKIE.length + 1))) as Stored);
  } catch {
    return null;
  }
}

export function readConsent(): Stored | null {
  try {
    const s = valid(JSON.parse(localStorage.getItem(KEY) ?? "null") as Stored | null);
    if (s) return s;
  } catch {
    /* localStorage bloqueado o inválido: usar la cookie */
  }
  return readCookie();
}

function newId(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") return crypto.randomUUID();
  } catch {
    /* respaldo abajo */
  }
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`;
}

function applyConsentMode(c: Cats) {
  const w = window as any;
  w.dataLayer = w.dataLayer || [];
  const gtag = w.gtag || function () { w.dataLayer.push(arguments); };
  gtag("consent", "update", {
    analytics_storage: c.analytics ? "granted" : "denied",
    ad_storage: c.marketing ? "granted" : "denied",
    ad_user_data: c.marketing ? "granted" : "denied",
    ad_personalization: c.marketing ? "granted" : "denied",
    functionality_storage: c.preferences ? "granted" : "denied",
    personalization_storage: c.preferences ? "granted" : "denied",
  });
  window.dispatchEvent(new CustomEvent("mm:consent", { detail: c }));
}

export function CookieConsent() {
  const { i18n } = useTranslation();
  const lang = ((i18n.language ?? "es").slice(0, 2) as L) in TXT ? ((i18n.language ?? "es").slice(0, 2) as L) : "es";
  const t = TXT[lang];
  const [open, setOpen] = useState(false);
  const [custom, setCustom] = useState(false);
  const [cats, setCats] = useState<Cats>({ preferences: false, analytics: false, marketing: false });

  useEffect(() => {
    const s = readConsent();
    if (s) {
      setCats(s);
      applyConsentMode(s);
    } else setOpen(true);
    const reopen = () => {
      const cur = readConsent();
      if (cur) setCats(cur);
      setCustom(true);
      setOpen(true);
    };
    window.addEventListener("mm:cookie-prefs", reopen);
    return () => window.removeEventListener("mm:cookie-prefs", reopen);
  }, []);

  async function save(c: Cats, action: "accept_all" | "reject_optional" | "custom") {
    setOpen(false);
    setCats(c);
    let prev: Stored | null = null;
    try { prev = readConsent(); } catch { /* sin comprobante previo */ }
    const stored: Stored = { ...c, version: CONSENT_VERSION, at: new Date().toISOString(), id: prev?.id ?? newId() };
    const json = JSON.stringify(stored);
    try { localStorage.setItem(KEY, json); } catch { /* lleno o bloqueado: queda la cookie */ }
    try {
      document.cookie = `${COOKIE}=${encodeURIComponent(json)}; path=/; max-age=31536000; SameSite=Lax; Secure`;
    } catch { /* sin cookies */ }
    try { applyConsentMode(c); } catch { /* no bloquear el cierre */ }
    try {
      const { supabase } = await import("@/integrations/supabase/client");
      await (supabase as any).from("cookie_consents").insert({
        consent_id: stored.id, notice_version: CONSENT_VERSION, necessary: true,
        preferences: c.preferences, analytics: c.analytics, marketing: c.marketing,
        action, lang,
      });
    } catch {
      /* el comprobante local sigue siendo válido */
    }
  }

  if (!open) return null;
  return (
    <div role="dialog" aria-modal="false" aria-labelledby="cc-title" className="fixed inset-x-2 bottom-[calc(72px+env(safe-area-inset-bottom,0px))] z-[70] mx-auto max-w-xl rounded-2xl border bg-card p-4 text-card-foreground shadow-xl lg:bottom-6">
      <h2 id="cc-title" className="text-base font-semibold">{t.title}</h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {t.body} <a href="/cookies" className="underline">{t.policy}</a>
      </p>
      {custom && (
        <div className="mt-3 space-y-2 text-sm">
          <div className="flex items-center justify-between"><span>{t.necessary}</span><Switch checked disabled aria-label={t.necessary} /></div>
          {(["preferences", "analytics", "marketing"] as const).map((k) => (
            <label key={k} className="flex items-center justify-between gap-3">
              <span>{t[k]}</span>
              <Switch checked={cats[k]} onCheckedChange={(v) => setCats({ ...cats, [k]: v })} aria-label={t[k]} />
            </label>
          ))}
        </div>
      )}
      <div className="mt-3 grid gap-2 sm:grid-cols-3">
        <Button variant="outline" onClick={() => save({ preferences: true, analytics: true, marketing: true }, "accept_all")}>{t.accept}</Button>
        <Button variant="outline" onClick={() => save({ preferences: false, analytics: false, marketing: false }, "reject_optional")}>{t.reject}</Button>
        {custom ? (
          <Button variant="outline" onClick={() => save(cats, "custom")}>{t.save}</Button>
        ) : (
          <Button variant="outline" onClick={() => setCustom(true)}>{t.config}</Button>
        )}
      </div>
    </div>
  );
}

/** Script para <head>: Consent Mode v2 con todo denegado antes de cualquier etiqueta. */
export const CONSENT_DEFAULT_SCRIPT = `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}window.gtag=window.gtag||gtag;gtag('consent','default',{ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',analytics_storage:'denied',functionality_storage:'denied',personalization_storage:'denied',security_storage:'granted',wait_for_update:500});`;
